import React, { useState, useEffect, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import MessagesAlertsPanel from './MessagesAlertsPanel';
import { Settings, Key, Building, Zap, FileText, Bell, Shield, DollarSign, Loader, CheckCircle, AlertCircle, MapPin, QrCode, UserCheck, Users, X } from 'lucide-react';

function openRadiusMap(lat, lng, radiusM) {
  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8"/>
  <meta name="viewport" content="width=device-width,initial-scale=1"/>
  <title>Delivery Zone — Hollypaul</title>
  <link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css"/>
  <script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"><\/script>
  <style>
    *{box-sizing:border-box;margin:0;padding:0}
    body{font-family:-apple-system,Arial,sans-serif;background:#111;color:#fff;display:flex;flex-direction:column;height:100vh}
    header{padding:14px 20px;background:#1a1a1a;border-bottom:1px solid #333;display:flex;align-items:center;justify-content:space-between;flex-shrink:0}
    .brand{font-size:1.1rem;font-weight:800;letter-spacing:.1em;color:#c9a84c}
    .info{font-size:.8rem;color:#888}
    #map{flex:1}
    .legend{position:absolute;bottom:24px;left:50%;transform:translateX(-50%);z-index:1000;background:rgba(17,17,17,.92);border:1px solid #333;border-radius:10px;padding:12px 20px;display:flex;align-items:center;gap:12px;font-size:.82rem;color:#ccc;backdrop-filter:blur(6px);white-space:nowrap}
    .swatch{width:16px;height:16px;border-radius:50%;border:2px solid #c9a84c;background:rgba(201,168,76,.15);flex-shrink:0}
  </style>
</head>
<body>
  <header>
    <span class="brand">HOLLYPAUL — DELIVERY ZONE</span>
    <span class="info">Centre: ${parseFloat(lat).toFixed(5)}, ${parseFloat(lng).toFixed(5)} &nbsp;·&nbsp; Radius: ${(radiusM/1000).toFixed(1)} km</span>
  </header>
  <div id="map"></div>
  <div class="legend"><div class="swatch"></div>Finish zone — ${(radiusM/1000).toFixed(1)} km radius</div>
  <script>
    var map = L.map('map').setView([${lat},${lng}],13);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',{attribution:'© OpenStreetMap contributors',maxZoom:18}).addTo(map);
    var circle = L.circle([${lat},${lng}],{radius:${radiusM},color:'#c9a84c',weight:2,opacity:.9,fillColor:'#c9a84c',fillOpacity:.08}).addTo(map);
    L.circleMarker([${lat},${lng}],{radius:6,color:'#c9a84c',fillColor:'#c9a84c',fillOpacity:1,weight:2}).addTo(map).bindPopup('<b>Zone centre</b>').openPopup();
    map.fitBounds(circle.getBounds(),{padding:[40,40]});
  <\/script>
</body>
</html>`;
  const blob = new Blob([html], { type: 'text/html' });
  const url  = URL.createObjectURL(blob);
  window.open(url, '_blank');
}

// ── QR Override Panel ─────────────────────────────────────────────────────────
function QROverridePanel({ personnel }) {
  const [driver, setDriver]       = useState('');
  const [busy, setBusy]           = useState(false);
  const [overrides, setOverrides] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [successMsg, setSuccessMsg]   = useState('');

  const today = new Date().toISOString().slice(0, 10);

  async function callOverride(action, extra = {}) {
    const res = await fetch('/.netlify/functions/qr-override', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action, date: today, ...extra }),
    });
    return res.json();
  }

  async function loadList() {
    setLoadingList(true);
    try {
      const data = await callOverride('list', { date: today });
      setOverrides(data.overrides || []);
    } catch {}
    finally { setLoadingList(false); }
  }

  useEffect(() => { loadList(); }, []); // eslint-disable-line

  async function handleGrant() {
    if (!driver) return;
    setBusy(true); setSuccessMsg('');
    try {
      const data = await callOverride('grant', { driver, grantedBy: 'ADMIN' });
      if (data.ok) {
        setSuccessMsg(`QR override granted for ${driver}. Their portal will unlock within 15 seconds.`);
        setDriver('');
        await loadList();
      } else {
        alert('Failed: ' + (data.error || 'unknown error'));
      }
    } catch (e) { alert('Error: ' + e.message); }
    finally { setBusy(false); }
  }

  async function handleRevoke(d) {
    if (!window.confirm(`Revoke QR override for ${d}?`)) return;
    await callOverride('revoke', { driver: d });
    await loadList();
  }

  const drivers = (personnel || [])
    .map(p => ({ login: (p.loginName || p.login || '').toUpperCase(), name: p.name || p.contactName || '' }))
    .filter(p => p.login)
    .sort((a, b) => a.login.localeCompare(b.login));

  const inputStyle = {
    width: '100%', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)',
    borderRadius: 'var(--radius)', padding: '9px 12px', color: 'var(--hp-text)',
    fontSize: '0.88rem', fontFamily: 'var(--font-mono)', boxSizing: 'border-box',
  };

  return (
    <>
      <h4 style={{ marginBottom: '4px' }}>QR Override</h4>
      <p style={{ fontSize: '0.8rem', color: 'var(--hp-dim)', marginBottom: '24px' }}>
        Grant a driver permission to start their shift without scanning the depot QR code.
        Use this when a driver is stuck at the depot and cannot scan the QR. Their portal
        unlocks automatically within 15 seconds.
      </p>

      {/* Grant form */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <label style={{ fontSize: '0.75rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>
            Driver
          </label>
          <select value={driver} onChange={e => setDriver(e.target.value)} style={inputStyle}>
            <option value="">— Select driver —</option>
            {drivers.map(d => (
              <option key={d.login} value={d.login}>{d.login}{d.name ? ` — ${d.name}` : ''}</option>
            ))}
          </select>
        </div>
        <button
          onClick={handleGrant}
          disabled={busy || !driver}
          style={{
            padding: '9px 20px', background: 'var(--hp-gold)', border: 'none',
            borderRadius: 'var(--radius)', color: 'var(--hp-black)', fontWeight: 700,
            fontSize: '0.875rem', cursor: busy || !driver ? 'not-allowed' : 'pointer',
            opacity: busy || !driver ? 0.6 : 1,
            display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap',
          }}
        >
          <UserCheck size={15} /> {busy ? 'Granting…' : 'Grant Override'}
        </button>
      </div>

      {successMsg && (
        <div style={{ background: 'rgba(52,211,153,0.12)', border: '1px solid rgba(52,211,153,0.3)', borderRadius: 'var(--radius)', padding: '12px 16px', color: 'var(--hp-green)', fontSize: '0.875rem', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span><CheckCircle size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />{successMsg}</span>
          <button onClick={() => setSuccessMsg('')} style={{ background: 'none', border: 'none', color: 'var(--hp-dim)', cursor: 'pointer' }}><X size={14} /></button>
        </div>
      )}

      {/* Today's overrides */}
      <div style={{ fontSize: '0.75rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
        Today's overrides ({overrides.length})
      </div>
      {loadingList && <div style={{ fontSize: '0.82rem', color: 'var(--hp-dim)' }}>Loading…</div>}
      {!loadingList && overrides.length === 0 && (
        <div style={{ fontSize: '0.82rem', color: 'var(--hp-dim)' }}>No overrides granted today.</div>
      )}
      {overrides.map(o => (
        <div key={o.driver} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', marginBottom: 8 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: '0.88rem', color: 'var(--hp-text)', fontFamily: 'var(--font-mono)' }}>{o.driver}</div>
            <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', marginTop: 2 }}>
              Granted {new Date(o.grantedAt).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}
              {o.used ? <span style={{ marginLeft: 8, color: 'var(--hp-green)' }}>✓ Used</span> : <span style={{ marginLeft: 8, color: '#f59e0b' }}>Pending</span>}
            </div>
          </div>
          <button onClick={() => handleRevoke(o.driver)} style={{ background: 'none', border: 'none', color: 'var(--hp-muted)', cursor: 'pointer', fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: 4 }}>
            <X size={13} /> Revoke
          </button>
        </div>
      ))}
    </>
  );
}

// ── Team Leader Access ─────────────────────────────────────────────────────
// Matrix of team leaders × portal functions. Admin ticks the functions each TL
// may use, then "Push to TL" saves it. The portal reads getFeatureAccess on
// login and shows exactly those tabs (scoped to the TL's team).
const TL_FEATURES = [
  { key: 'afternoon_check', label: 'Afternoon Check', type: 'bool' },
  { key: 'logged_on',       label: 'Logged-On Drivers', type: 'bool' },
  { key: 'leave_calendar',  label: 'Leave Calendar', type: 'bool' },
  { key: 'driver_routes',   label: 'Driver Routes', type: 'scope' },
  { key: 'status',          label: 'Status Page', type: 'bool' },
];
const TL_DEFAULT_FEATURES = { afternoon_check: false, logged_on: false, leave_calendar: false, driver_routes: '', status: false };

// Map a Team_Leaders 'Route' value to the area-code scope the portal uses to
// filter Afternoon Check / Logged-On / Status / Leave. 'ALL' or blank → all teams.
function routeToAreas(route) {
  const r = (route || '').trim().toLowerCase();
  if (r === 'city')        return ['CITY'];
  if (r === 'broadway')    return ['BWAY'];
  if (r === 'darlinghurst') return ['DARL'];
  return 'ALL';
}

function normaliseFeatures(f) {
  return {
    afternoon_check: !!(f && f.afternoon_check),
    logged_on:       !!(f && f.logged_on),
    leave_calendar:  !!(f && f.leave_calendar),
    status:          !!(f && f.status),
    driver_routes:   (f && ['all', 'team', 'driver'].includes(f.driver_routes)) ? f.driver_routes : '',
  };
}

function TeamLeaderAccessPanel({ addNotification, currentUser }) {
  const [teamLeaders, setTeamLeaders] = useState([]);
  const [edits, setEdits]   = useState({}); // login -> features (local, editable)
  const [saved, setSaved]   = useState({}); // login -> features (last pushed)
  const [loading, setLoading] = useState(true);
  const [pushing, setPushing] = useState(''); // login currently being pushed

  async function callAS(action, extra = {}) {
    const res = await fetch('/.netlify/functions/appsscript', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action, ...extra }),
    });
    return res.json();
  }
  // Targets a specific Netlify function — for actions migrated off Apps Script.
  async function callFn(url, action, extra = {}) {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action, ...extra }),
    });
    return res.json();
  }

  async function load() {
    setLoading(true);
    try {
      const [tlRes, accRes] = await Promise.all([
        callAS('getTeamLeaders'),
        callFn('/.netlify/functions/feature-access-sb', 'getFeatureAccess'),
      ]);
      const tls = (tlRes.teamLeaders || []).filter(t => t.login);
      const map = accRes.map || {};
      const e = {}, s = {};
      tls.forEach(t => {
        const nf = normaliseFeatures(map[t.login]);
        e[t.login] = { ...nf };
        s[t.login] = { ...nf };
      });
      setTeamLeaders(tls);
      setEdits(e);
      setSaved(s);
    } catch (err) {
      addNotification('Could not load Team Leader access', 'error');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []); // eslint-disable-line

  const setFeature = (login, key, value) => {
    setEdits(prev => ({ ...prev, [login]: { ...(prev[login] || TL_DEFAULT_FEATURES), [key]: value } }));
  };

  const isDirty = (login) =>
    JSON.stringify(edits[login] || TL_DEFAULT_FEATURES) !== JSON.stringify(saved[login] || TL_DEFAULT_FEATURES);

  async function pushToTL(login) {
    setPushing(login);
    try {
      const tl = teamLeaders.find(t => t.login === login);
      const res = await callFn('/.netlify/functions/feature-access-sb', 'setFeatureAccess', {
        by: (currentUser?.login || '').toUpperCase(),
        login,
        features: { ...normaliseFeatures(edits[login]), areas: routeToAreas(tl && tl.route) },
      });
      if (res.success) {
        setSaved(prev => ({ ...prev, [login]: { ...normaliseFeatures(edits[login]) } }));
        addNotification(`Access pushed to ${login} — appears on their next portal refresh`, 'success');
      } else {
        addNotification(res.error || 'Push failed', 'error');
      }
    } catch (err) {
      addNotification('Push failed: ' + err.message, 'error');
    } finally {
      setPushing('');
    }
  }

  const th = { fontSize: '0.68rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.05em', padding: '8px 10px', textAlign: 'center', whiteSpace: 'nowrap' };
  const td = { padding: '10px', textAlign: 'center', borderTop: '1px solid var(--hp-border)' };
  const scopeStyle = { background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: '5px 8px', color: 'var(--hp-text)', fontSize: '0.78rem', fontFamily: 'var(--font-mono)' };

  return (
    <>
      <h4 style={{ marginBottom: '4px' }}>Team Leader Access</h4>
      <p style={{ fontSize: '0.8rem', color: 'var(--hp-dim)', marginBottom: '20px' }}>
        Nominate which portal functions each team leader can use. Tick the functions, set the Driver Routes scope,
        then <strong style={{ color: 'var(--hp-text)' }}>Push to TL</strong>. The tabs appear on their portal the next
        time they open it — each function is scoped to their own team.
      </p>

      {loading && <div style={{ fontSize: '0.82rem', color: 'var(--hp-dim)' }}>Loading team leaders…</div>}

      {!loading && teamLeaders.length === 0 && (
        <div style={{ fontSize: '0.82rem', color: 'var(--hp-dim)' }}>
          No team leaders found in the Team_Leaders sheet.
        </div>
      )}

      {!loading && teamLeaders.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
            <thead>
              <tr>
                <th style={{ ...th, textAlign: 'left' }}>Team Leader</th>
                {TL_FEATURES.map(f => <th key={f.key} style={th}>{f.label}</th>)}
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {teamLeaders.map(tl => {
                const f = edits[tl.login] || TL_DEFAULT_FEATURES;
                const dirty = isDirty(tl.login);
                return (
                  <tr key={tl.login}>
                    <td style={{ ...td, textAlign: 'left' }}>
                      <div style={{ fontWeight: 700, fontSize: '0.85rem', color: 'var(--hp-text)' }}>{tl.name || tl.login}</div>
                      <div style={{ fontSize: '0.7rem', color: 'var(--hp-dim)', fontFamily: 'var(--font-mono)' }}>
                        {tl.login}{tl.route ? ` · ${tl.route}` : ''}
                      </div>
                    </td>
                    {TL_FEATURES.map(ft => (
                      <td key={ft.key} style={td}>
                        {ft.type === 'bool' ? (
                          <input
                            type="checkbox"
                            checked={!!f[ft.key]}
                            onChange={e => setFeature(tl.login, ft.key, e.target.checked)}
                            style={{ width: 17, height: 17, cursor: 'pointer', accentColor: 'var(--hp-gold)' }}
                          />
                        ) : (
                          <select
                            value={f[ft.key] || ''}
                            onChange={e => setFeature(tl.login, ft.key, e.target.value)}
                            style={scopeStyle}
                          >
                            <option value="">Off</option>
                            <option value="driver">Driver</option>
                            <option value="team">Team</option>
                            <option value="all">All</option>
                          </select>
                        )}
                      </td>
                    ))}
                    <td style={td}>
                      <button
                        onClick={() => pushToTL(tl.login)}
                        disabled={!dirty || pushing === tl.login}
                        style={{
                          padding: '7px 14px',
                          background: dirty ? 'var(--hp-gold)' : 'var(--hp-surface)',
                          border: dirty ? 'none' : '1px solid var(--hp-border)',
                          borderRadius: 'var(--radius)',
                          color: dirty ? 'var(--hp-black)' : 'var(--hp-dim)',
                          fontWeight: 700, fontSize: '0.78rem',
                          cursor: (!dirty || pushing === tl.login) ? 'default' : 'pointer',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {pushing === tl.login ? 'Pushing…' : dirty ? 'Push to TL' : 'Saved'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', marginTop: 16, lineHeight: 1.7 }}>
        <strong style={{ color: 'var(--hp-text)' }}>Driver Routes scope:</strong> Driver = own routes only ·
        Team = all routes on their team · All = every route.
        Afternoon Check, Logged-On Drivers and Status are scoped to the team leader's own team.
      </p>
    </>
  );
}

const COMPANY_STORE = 'hp-company-details';
const COMPANY_DEFAULTS = {
  principalCompany: 'PH Bouveng Pty Ltd',
  principalAbn:     '12 093 039 297',
  personnelCompany: 'Hollypaul Recruitment Pty Ltd',
  personnelAbn:     '60 651 664 885',
  assetCompany:     'Hollypaul Leasing Pty Ltd',
  assetAbn:         '29 522 075 794',
  address:          'PO Box 1710, Warriewood NSW 2102',
  state:            'NSW',
};

function CompanyDetailsPanel({ addNotification }) {
  const [form, setForm] = useState(() => {
    try { return { ...COMPANY_DEFAULTS, ...JSON.parse(localStorage.getItem(COMPANY_STORE) || '{}') }; }
    catch { return COMPANY_DEFAULTS; }
  });

  function save() {
    localStorage.setItem(COMPANY_STORE, JSON.stringify(form));
    addNotification('Company details saved', 'success');
  }

  const f = (key) => ({
    value: form[key],
    onChange: e => setForm(p => ({ ...p, [key]: e.target.value })),
  });

  return (
    <>
      <h4 style={{ marginBottom: '20px' }}>Company Details</h4>
      <div className="grid-2">
        <div className="form-group">
          <label className="form-label">Principal Company</label>
          <input className="form-input" {...f('principalCompany')} />
        </div>
        <div className="form-group">
          <label className="form-label">Principal ABN</label>
          <input className="form-input" {...f('principalAbn')} placeholder="xx xxx xxx xxx" />
        </div>
        <div className="form-group">
          <label className="form-label">Personnel Company</label>
          <input className="form-input" {...f('personnelCompany')} />
        </div>
        <div className="form-group">
          <label className="form-label">Personnel ABN</label>
          <input className="form-input" {...f('personnelAbn')} placeholder="xx xxx xxx xxx" />
        </div>
        <div className="form-group">
          <label className="form-label">Asset Company</label>
          <input className="form-input" {...f('assetCompany')} />
        </div>
        <div className="form-group">
          <label className="form-label">Asset ABN</label>
          <input className="form-input" {...f('assetAbn')} placeholder="xx xxx xxx xxx" />
        </div>
        <div className="form-group">
          <label className="form-label">Business Address</label>
          <input className="form-input" {...f('address')} placeholder="Street address" />
        </div>
        <div className="form-group">
          <label className="form-label">State / Territory</label>
          <select className="form-select" {...f('state')}>
            {['NSW','VIC','QLD','SA','WA','TAS','ACT','NT'].map(s => <option key={s}>{s}</option>)}
          </select>
        </div>
      </div>
      <button className="btn btn-primary" onClick={save}>Save Changes</button>
    </>
  );
}

export default function SettingsPage() {
  const { xeroConnected, setXeroConnected, addNotification, sheetData, personnel, currentUser } = useApp();
  const [activeTab, setActiveTab] = useState('company');

  // ── Payroll Entry form state ─────────────────────────────────────────────
  const [payPeriod, setPayPeriod]         = useState('');
  const [nightSortHours, setNightSortHours] = useState('');
  const [grossWages, setGrossWages]       = useState('');
  const [taxWithholding, setTaxWithholding] = useState('');
  const [superannuation, setSuperannuation] = useState('');
  const [totalCost, setTotalCost]         = useState('');
  const [totalCostManual, setTotalCostManual] = useState(false);
  const [xeroRef, setXeroRef]             = useState('');
  const [notes, setNotes]                 = useState('');
  const [saving, setSaving]               = useState(false);
  const [saveResult, setSaveResult]       = useState(null); // { ok, msg }

  // ── Geofence Settings form state ─────────────────────────────────────────
  const [geoRadiusM, setGeoRadiusM]           = useState(150);
  const [geoEnabled, setGeoEnabled]           = useState(true);
  const [driverQrRequired, setDriverQrRequired] = useState(false);
  const [sorterQrRequired, setSorterQrRequired] = useState(false);
  const [geoSaving, setGeoSaving]             = useState(false);
  const [geoSaveResult, setGeoSaveResult]     = useState(null); // { ok, msg }

  // Finish zone
  const [finishZoneEnabled, setFinishZoneEnabled] = useState(false);
  const [finishZoneLat, setFinishZoneLat]         = useState('-33.882');
  const [finishZoneLng, setFinishZoneLng]         = useState('151.195');
  const [finishZoneRadiusM, setFinishZoneRadiusM] = useState(4000);
  const [finishGpsCapturing, setFinishGpsCapturing] = useState(false);

  // Per-depot coordinate overrides — keyed by depot id (e.g. 'botany', 'alexandria', 'newtown')
  const DEPOT_DEFAULTS = [
    { id: 'botany',     name: 'Botany',     lat: '-33.95026',   lng: '151.1945'    },
    { id: 'alexandria', name: 'Alexandria', lat: '-33.915597',  lng: '151.197872'  },
    { id: 'newtown',    name: 'Newtown',    lat: '-33.8997',    lng: '151.1754'    },
  ];
  const [depotPins, setDepotPins] = useState(DEPOT_DEFAULTS);
  const [gpsCapturing, setGpsCapturing] = useState(null); // depot id currently capturing GPS

  // Unique period options from AP_Revenue tab
  const periodOptions = useMemo(() => {
    const seen = new Set();
    return (sheetData?.apRevenue || []).filter(r => {
      if (!r.Period_Label || seen.has(r.Period_Label)) return false;
      seen.add(r.Period_Label);
      return true;
    });
  }, [sheetData]);

  // Auto-calculate Total Cost = Gross Wages + Super (unless user overrode it)
  useEffect(() => {
    if (totalCostManual) return;
    const g = parseFloat(grossWages) || 0;
    const t = parseFloat(taxWithholding) || 0;
    const s = parseFloat(superannuation) || 0;
    setTotalCost(g + t + s > 0 ? (g + t + s).toFixed(2) : '');
  }, [grossWages, taxWithholding, superannuation, totalCostManual]);

  const handleTotalCostChange = (e) => {
    setTotalCost(e.target.value);
    setTotalCostManual(true);
  };

  const recalcTotal = () => {
    setTotalCostManual(false);
    const g = parseFloat(grossWages) || 0;
    const t = parseFloat(taxWithholding) || 0;
    const s = parseFloat(superannuation) || 0;
    setTotalCost(g + t + s > 0 ? (g + t + s).toFixed(2) : '');
  };

  // Seed geofence form — fetch directly from geofence-settings function (source of truth)
  useEffect(() => {
    fetch('/.netlify/functions/geofence-settings')
      .then(r => r.json())
      .then(data => {
        if (!data.success) return;
        const gs = data.settings;
        setGeoRadiusM(gs.geofenceRadiusM ?? 150);
        setGeoEnabled(gs.geofenceEnabled ?? true);
        setDriverQrRequired(gs.driverQrRequired ?? false);
        setSorterQrRequired(gs.sorterQrRequired ?? gs.qrRequired ?? false);
        if (gs.depotPins) {
          setDepotPins(prev => prev.map(d => {
            const saved = gs.depotPins[d.id];
            return saved ? { ...d, lat: String(saved.lat), lng: String(saved.lng) } : d;
          }));
        }
        if (gs.finishZone) {
          const fz = gs.finishZone;
          setFinishZoneEnabled(fz.finishZoneEnabled ?? false);
          setFinishZoneLat(String(fz.finishZoneLat ?? -33.882));
          setFinishZoneLng(String(fz.finishZoneLng ?? 151.195));
          setFinishZoneRadiusM(fz.finishZoneRadiusM ?? 4000);
        }
      })
      .catch(() => {}); // silently fall back to defaults on error
  }, []);

  function captureGPS(depotId) {
    if (!navigator.geolocation) return;
    setGpsCapturing(depotId);
    navigator.geolocation.getCurrentPosition(
      pos => {
        setDepotPins(prev => prev.map(d =>
          d.id === depotId
            ? { ...d, lat: pos.coords.latitude.toFixed(7), lng: pos.coords.longitude.toFixed(7) }
            : d
        ));
        setGpsCapturing(null);
      },
      () => { setGpsCapturing(null); },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  }

  const handleSaveGeofence = async () => {
    setGeoSaving(true);
    setGeoSaveResult(null);
    try {
      const res = await fetch('/.netlify/functions/geofence-settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action:           'saveGeofenceSettings',
          radiusM:          parseInt(geoRadiusM) || 150,
          geofenceEnabled:  geoEnabled,
          driverQrRequired: driverQrRequired,
          sorterQrRequired: sorterQrRequired,
          depotPins:       Object.fromEntries(
            depotPins.map(d => [d.id, { lat: parseFloat(d.lat), lng: parseFloat(d.lng) }])
          ),
          finishZone: {
            finishZoneEnabled,
            finishZoneLat:     parseFloat(finishZoneLat)    || -33.882,
            finishZoneLng:     parseFloat(finishZoneLng)    || 151.195,
            finishZoneRadiusM: parseInt(finishZoneRadiusM)  || 4000,
          },
        }),
      });
      const data = await res.json();
      if (data.success) {
        setGeoSaveResult({ ok: true, msg: 'Geofence settings saved.' });
        addNotification('Geofence settings saved', 'success');
        // Re-fetch to confirm what was actually written
        fetch('/.netlify/functions/geofence-settings')
          .then(r => r.json())
          .then(d => {
            if (d.success && d.settings.depotPins) {
              setDepotPins(prev => prev.map(dp2 => {
                const s = d.settings.depotPins[dp2.id];
                return s ? { ...dp2, lat: String(s.lat), lng: String(s.lng) } : dp2;
              }));
            }
          }).catch(() => {});
      } else {
        setGeoSaveResult({ ok: false, msg: data.error || 'Save failed.' });
      }
    } catch (err) {
      setGeoSaveResult({ ok: false, msg: err.message });
    } finally {
      setGeoSaving(false);
    }
  };

  const selectedPeriod = periodOptions.find(r => r.Period_Label === payPeriod) || {};

  const handleSavePayroll = async () => {
    if (!payPeriod) {
      setSaveResult({ ok: false, msg: 'Please select a pay period.' });
      return;
    }
    setSaving(true);
    setSaveResult(null);
    try {
      const res = await fetch('/.netlify/functions/appsscript', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action:           'savePayroll',
          Period_Label:     payPeriod,
          Period_Start:     selectedPeriod.Period_Start || '',
          Period_End:       selectedPeriod.Period_End   || '',
          Night_Sort_Hours:  nightSortHours,
          Gross_Wages:       grossWages,
          Tax_Withholding:   taxWithholding,
          Superannuation:    superannuation,
          Total_Cost:        totalCost,
          Xero_Ref:         xeroRef,
          Notes:            notes,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setSaveResult({ ok: true, msg: 'Payroll entry saved successfully.' });
        // reset form
        setPayPeriod(''); setNightSortHours(''); setGrossWages('');
        setTaxWithholding(''); setSuperannuation(''); setTotalCost('');
        setTotalCostManual(false); setXeroRef(''); setNotes('');
      } else {
        setSaveResult({ ok: false, msg: data.error || 'Save failed — check Apps Script logs.' });
      }
    } catch (err) {
      setSaveResult({ ok: false, msg: err.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="page-header">
        <div className="page-header-left">
          <h2>Settings</h2>
          <p>System configuration — Hollypaul App Engine</p>
        </div>
      </div>

      <div className="page-body">
        <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: '24px' }}>
          {/* Settings nav */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            {[
              { id: 'company',      icon: Building,     label: 'Company' },
              { id: 'xero',         icon: Zap,          label: 'Xero Integration' },
              { id: 'payroll',      icon: DollarSign,   label: 'Payroll Entry' },
              { id: 'password',     icon: Key,          label: 'Site Password' },
              { id: 'contracts',    icon: FileText,      label: 'Contract Templates' },
              { id: 'geofence',     icon: MapPin,       label: 'Geofence' },
              { id: 'qroverride',   icon: QrCode,       label: 'QR Override' },
              { id: 'team-leaders', icon: Users,        label: 'Team Leader Access' },
              { id: 'notifications',icon: Bell,         label: 'Messages & Alerts' },
              { id: 'security',     icon: Shield,       label: 'Security' },
            ].map(({ id, icon: Icon, label }) => (
              <button
                key={id}
                className={`nav-link${activeTab === id ? ' active' : ''}`}
                style={{ border: 'none', cursor: 'pointer', textAlign: 'left' }}
                onClick={() => setActiveTab(id)}
              >
                <Icon size={15} /> {label}
              </button>
            ))}
          </div>

          {/* Settings panel */}
          <div className="card">
            {activeTab === 'company' && (
              <CompanyDetailsPanel addNotification={addNotification} />
            )}

            {activeTab === 'xero' && (
              <>
                <h4 style={{ marginBottom: '4px' }}>Xero Integration</h4>
                <p style={{ fontSize: '0.8rem', color: 'var(--hp-dim)', marginBottom: '24px' }}>
                  Configure OAuth credentials in Netlify Environment Variables for security.
                </p>
                <div style={{ padding: '16px', background: 'var(--hp-surface)', borderRadius: 'var(--radius)', marginBottom: '24px', fontSize: '0.8rem', color: 'var(--hp-dim)', lineHeight: '1.8' }}>
                  <strong style={{ color: 'var(--hp-text)' }}>Required Netlify Env Vars:</strong><br/>
                  <span style={{ color: 'var(--hp-gold)', fontFamily: 'var(--font-mono)' }}>XERO_CLIENT_ID</span> — From Xero developer portal<br/>
                  <span style={{ color: 'var(--hp-gold)', fontFamily: 'var(--font-mono)' }}>XERO_CLIENT_SECRET</span> — From Xero developer portal<br/>
                  <span style={{ color: 'var(--hp-gold)', fontFamily: 'var(--font-mono)' }}>XERO_REDIRECT_URI</span> — https://yoursite.netlify.app/auth/xero<br/>
                  <span style={{ color: 'var(--hp-gold)', fontFamily: 'var(--font-mono)' }}>SITE_PASSWORD</span> — Site access password
                </div>
                <div className="grid-2">
                  <div className="form-group">
                    <label className="form-label">Default Invoice Type</label>
                    <select className="form-select">
                      <option>ACCPAY (Accounts Payable — RCTI)</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Invoice Status</label>
                    <select className="form-select">
                      <option>DRAFT</option>
                      <option>SUBMITTED</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Payment Terms (days)</label>
                    <input className="form-input" type="number" defaultValue="7" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">AP Account Code</label>
                    <input className="form-input" defaultValue="200" />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button className="btn btn-primary" onClick={() => { setXeroConnected(!xeroConnected); addNotification(xeroConnected ? 'Xero disconnected' : 'Xero connected (dev)', xeroConnected ? 'info' : 'success'); }}>
                    <Zap size={14} /> {xeroConnected ? 'Disconnect Xero' : 'Connect Xero'}
                  </button>
                  <button className="btn btn-secondary" onClick={() => addNotification('Settings saved', 'success')}>Save Settings</button>
                </div>
              </>
            )}

            {activeTab === 'payroll' && (
              <>
                <h4 style={{ marginBottom: '4px' }}>Payroll Entry</h4>
                <p style={{ fontSize: '0.8rem', color: 'var(--hp-dim)', marginBottom: '24px' }}>
                  Record payroll costs per pay period. Saved to the Payroll tab in the source spreadsheet.
                </p>

                {/* Pay Period */}
                <div className="form-group" style={{ marginBottom: '20px' }}>
                  <label className="form-label">Pay Period</label>
                  <select
                    className="form-select"
                    value={payPeriod}
                    onChange={e => setPayPeriod(e.target.value)}
                  >
                    <option value="">— Select a period —</option>
                    {periodOptions.length === 0 && (
                      <option disabled value="">Loading periods…</option>
                    )}
                    {periodOptions.map(r => (
                      <option key={r.Period_Label} value={r.Period_Label}>{r.Period_Label}</option>
                    ))}
                  </select>
                  {payPeriod && selectedPeriod.Period_Start && (
                    <div style={{ marginTop: '6px', fontSize: '0.75rem', color: 'var(--hp-dim)' }}>
                      {selectedPeriod.Period_Start} → {selectedPeriod.Period_End}
                    </div>
                  )}
                </div>

                {/* Numeric fields */}
                <div className="grid-2" style={{ marginBottom: '4px' }}>
                  <div className="form-group">
                    <label className="form-label">Night Sort Hours</label>
                    <input
                      className="form-input"
                      type="number"
                      min="0"
                      step="0.5"
                      placeholder="0"
                      value={nightSortHours}
                      onChange={e => setNightSortHours(e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Gross Wages ($)</label>
                    <input
                      className="form-input"
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      value={grossWages}
                      onChange={e => setGrossWages(e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Tax / PAYG Withholding ($)</label>
                    <input
                      className="form-input"
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      value={taxWithholding}
                      onChange={e => setTaxWithholding(e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Superannuation ($)</label>
                    <input
                      className="form-input"
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      value={superannuation}
                      onChange={e => setSuperannuation(e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>Total Cost ($)</span>
                      {totalCostManual && (
                        <button
                          onClick={recalcTotal}
                          style={{ background: 'none', border: 'none', color: 'var(--hp-gold)', fontSize: '0.7rem', cursor: 'pointer', padding: 0, fontFamily: 'var(--font-body)' }}
                        >
                          ↺ recalculate
                        </button>
                      )}
                      {!totalCostManual && (
                        <span style={{ fontSize: '0.7rem', color: 'var(--hp-dim)' }}>auto-calculated</span>
                      )}
                    </label>
                    <input
                      className="form-input"
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      value={totalCost}
                      onChange={handleTotalCostChange}
                      style={totalCostManual ? { borderColor: 'var(--hp-gold)' } : {}}
                    />
                  </div>
                </div>

                {/* Optional fields */}
                <div className="grid-2" style={{ marginBottom: '20px' }}>
                  <div className="form-group">
                    <label className="form-label">Xero Ref <span style={{ color: 'var(--hp-dim)', fontWeight: 400 }}>(optional)</span></label>
                    <input
                      className="form-input"
                      type="text"
                      placeholder="e.g. INV-1234"
                      value={xeroRef}
                      onChange={e => setXeroRef(e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Notes <span style={{ color: 'var(--hp-dim)', fontWeight: 400 }}>(optional)</span></label>
                    <input
                      className="form-input"
                      type="text"
                      placeholder="Any additional notes"
                      value={notes}
                      onChange={e => setNotes(e.target.value)}
                    />
                  </div>
                </div>

                {/* Save result banner */}
                {saveResult && (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    padding: '12px 16px',
                    marginBottom: '16px',
                    borderRadius: 'var(--radius)',
                    background: saveResult.ok ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
                    border: `1px solid ${saveResult.ok ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}`,
                    color: saveResult.ok ? 'var(--hp-green)' : 'var(--hp-red)',
                    fontSize: '0.875rem',
                  }}>
                    {saveResult.ok ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
                    {saveResult.msg}
                  </div>
                )}

                <button
                  className="btn btn-primary"
                  onClick={handleSavePayroll}
                  disabled={saving}
                >
                  {saving ? <><Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> Saving…</> : <><DollarSign size={14} /> Save Payroll Entry</>}
                </button>
              </>
            )}

            {activeTab === 'password' && (
              <>
                <h4 style={{ marginBottom: '4px' }}>Site Password</h4>
                <p style={{ fontSize: '0.8rem', color: 'var(--hp-dim)', marginBottom: '24px' }}>
                  The site password is set via the <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--hp-gold)' }}>SITE_PASSWORD</span> environment variable in Netlify.
                  Changing it here will update the env var (requires a Netlify re-deploy to take effect).
                </p>
                <div className="form-group" style={{ maxWidth: '360px' }}>
                  <label className="form-label">New Password</label>
                  <input className="form-input" type="password" placeholder="Enter new site password" />
                </div>
                <div className="form-group" style={{ maxWidth: '360px' }}>
                  <label className="form-label">Confirm Password</label>
                  <input className="form-input" type="password" placeholder="Confirm new password" />
                </div>
                <div style={{ padding: '14px 16px', background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.2)', borderRadius: 'var(--radius)', marginBottom: '16px', fontSize: '0.8rem', color: 'var(--hp-dim)' }}>
                  ⚡ The edge function checks <span style={{ fontFamily: 'var(--font-mono)' }}>SITE_PASSWORD</span> at runtime.
                  Update it in Netlify → Site → Environment variables → <span style={{ fontFamily: 'var(--font-mono)' }}>SITE_PASSWORD</span>.
                  Trigger a redeploy or the change takes effect on next edge function cold start.
                </div>
                <button className="btn btn-primary" onClick={() => addNotification('Password updated — redeploy required', 'success')}>Update Password</button>
              </>
            )}

            {activeTab === 'contracts' && (
              <>
                <h4 style={{ marginBottom: '4px' }}>Contract Templates</h4>
                <p style={{ fontSize: '0.8rem', color: 'var(--hp-dim)', marginBottom: '24px' }}>
                  Manage the agreement templates used for subcontractors and vehicle leases.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {[
                    { name: 'Subcontractor Agreement', version: 'v2.0', source: 'Modern Subcontractor Agreement (external)' },
                    { name: 'Vehicle Lease Agreement', version: 'v1.2', source: 'Built-in — Hollypaul Leasing Pty Ltd' },
                  ].map(t => (
                    <div key={t.name} style={{
                      padding: '16px', background: 'var(--hp-surface)',
                      border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)',
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    }}>
                      <div>
                        <div style={{ fontWeight: 600, marginBottom: '2px' }}>{t.name}</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--hp-dim)' }}>{t.version} · {t.source}</div>
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => addNotification(`Editing: ${t.name}`)}>Edit</button>
                        <button className="btn btn-secondary btn-sm" onClick={() => addNotification(`Downloading: ${t.name}`)}>Download</button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {activeTab === 'geofence' && (
              <>
                <h4 style={{ marginBottom: '4px' }}>Geofence Settings</h4>
                <p style={{ fontSize: '0.8rem', color: 'var(--hp-dim)', marginBottom: '24px' }}>
                  Controls whether drivers must be near a depot to start a shift, and whether a depot QR scan is required — set independently for Drivers and Night Sort.
                </p>

                {/* Toggles */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' }}>
                  {[
                    { label: 'Require GPS geofence',            desc: 'Driver must be within the required distance of a depot to start their shift.', value: geoEnabled,       set: setGeoEnabled },
                    { label: 'Require QR scan — Drivers',       desc: 'Drivers must scan the depot QR code before starting their shift.',              value: driverQrRequired, set: setDriverQrRequired },
                    { label: 'Require QR scan — Night Sort',    desc: 'Night sorters must scan the depot QR code to clock on and off.',                value: sorterQrRequired, set: setSorterQrRequired },
                  ].map(({ label, desc, value, set }) => (
                    <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '16px', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)' }}>
                      <div style={{ flex: 1, paddingRight: '16px' }}>
                        <div style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: '4px' }}>{label}</div>
                        <div style={{ fontSize: '0.78rem', color: 'var(--hp-dim)' }}>{desc}</div>
                      </div>
                      <button
                        onClick={() => set(v => !v)}
                        style={{
                          flexShrink: 0,
                          width: 44, height: 24,
                          borderRadius: 12,
                          border: 'none',
                          cursor: 'pointer',
                          background: value ? 'var(--hp-green)' : 'var(--hp-border)',
                          position: 'relative',
                          transition: 'background 200ms',
                        }}
                      >
                        <span style={{
                          position: 'absolute',
                          top: 3, left: value ? 23 : 3,
                          width: 18, height: 18,
                          borderRadius: '50%',
                          background: 'var(--hp-black)',
                          transition: 'left 200ms',
                        }} />
                      </button>
                    </div>
                  ))}
                </div>

                {/* Per-depot PIN overrides */}
                <div style={{ marginBottom: '24px' }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '12px' }}>
                    Depot GPS Pins
                  </div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', marginBottom: '16px', lineHeight: 1.5 }}>
                    Set the exact GPS pin for each depot. Walk to the warehouse gate on your phone, then tap <strong>Use my location</strong> to drop the pin precisely.
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {depotPins.map(depot => (
                      <div key={depot.id} style={{ background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: '16px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                          <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{depot.name} Depot</div>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            {depot.lat && depot.lng && (
                              <a
                                href={`https://www.google.com/maps/search/?api=1&query=${depot.lat},${depot.lng}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  display: 'flex', alignItems: 'center', gap: '5px',
                                  padding: '6px 12px',
                                  background: 'transparent',
                                  border: '1px solid var(--hp-border)',
                                  borderRadius: 'var(--radius)',
                                  color: 'var(--hp-dim)',
                                  fontSize: '0.78rem', fontWeight: 600,
                                  textDecoration: 'none',
                                  fontFamily: 'var(--font-body)',
                                }}
                              >
                                <MapPin size={13} /> Check map
                              </a>
                            )}
                            <button
                              onClick={() => captureGPS(depot.id)}
                              disabled={gpsCapturing === depot.id}
                              style={{
                                display: 'flex', alignItems: 'center', gap: '6px',
                                padding: '6px 12px',
                                background: gpsCapturing === depot.id ? 'var(--hp-border)' : 'rgba(201,168,76,0.12)',
                                border: '1px solid rgba(201,168,76,0.35)',
                                borderRadius: 'var(--radius)',
                                color: 'var(--hp-gold)',
                                cursor: gpsCapturing === depot.id ? 'not-allowed' : 'pointer',
                                fontSize: '0.78rem', fontWeight: 600,
                                fontFamily: 'var(--font-body)',
                              }}
                            >
                              <MapPin size={13} />
                              {gpsCapturing === depot.id ? 'Getting location…' : 'Use my location'}
                            </button>
                          </div>
                        </div>
                        <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                          <div>
                            <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '5px' }}>Latitude</label>
                            <input
                              className="form-input"
                              type="text"
                              inputMode="decimal"
                              placeholder="-33.9462426"
                              value={depot.lat}
                              onChange={e => setDepotPins(prev => prev.map(d => d.id === depot.id ? { ...d, lat: e.target.value } : d))}
                              style={{ fontFamily: 'var(--font-mono)', fontSize: '0.85rem' }}
                            />
                          </div>
                          <div>
                            <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '5px' }}>Longitude</label>
                            <input
                              className="form-input"
                              type="text"
                              inputMode="decimal"
                              placeholder="151.1898624"
                              value={depot.lng}
                              onChange={e => setDepotPins(prev => prev.map(d => d.id === depot.id ? { ...d, lng: e.target.value } : d))}
                              style={{ fontFamily: 'var(--font-mono)', fontSize: '0.85rem' }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Finish zone */}
                <div style={{ marginBottom: '24px' }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '12px' }}>
                    Finish Zone
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '16px', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', marginBottom: '12px' }}>
                    <div style={{ flex: 1, paddingRight: 16 }}>
                      <div style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: 4 }}>Require finish within delivery zone</div>
                      <div style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', lineHeight: 1.5 }}>Driver must be within the delivery area to submit their finish time. Location is recorded at the moment of submission.</div>
                    </div>
                    <button
                      onClick={() => setFinishZoneEnabled(v => !v)}
                      style={{ flexShrink: 0, width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer', background: finishZoneEnabled ? 'var(--hp-green)' : 'var(--hp-border)', position: 'relative', transition: 'background 200ms' }}
                    >
                      <span style={{ position: 'absolute', top: 3, left: finishZoneEnabled ? 23 : 3, width: 18, height: 18, borderRadius: '50%', background: 'var(--hp-black)', transition: 'left 200ms' }} />
                    </button>
                  </div>

                  <div style={{ opacity: finishZoneEnabled ? 1 : 0.4, pointerEvents: finishZoneEnabled ? 'auto' : 'none' }}>
                    {/* Zone centre */}
                    <div style={{ background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: '16px', marginBottom: '10px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                        <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>Zone Centre</div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          {finishZoneLat && finishZoneLng && (
                            <button
                              onClick={() => openRadiusMap(finishZoneLat, finishZoneLng, parseInt(finishZoneRadiusM) || 4000)}
                              style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', background: 'transparent', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', color: 'var(--hp-dim)', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-body)' }}
                            >
                              <MapPin size={13} /> Check radius
                            </button>
                          )}
                          <button
                            onClick={() => {
                              setFinishGpsCapturing(true);
                              navigator.geolocation.getCurrentPosition(
                                pos => { setFinishZoneLat(pos.coords.latitude.toFixed(7)); setFinishZoneLng(pos.coords.longitude.toFixed(7)); setFinishGpsCapturing(false); },
                                () => setFinishGpsCapturing(false),
                                { enableHighAccuracy: true, timeout: 15000 }
                              );
                            }}
                            disabled={finishGpsCapturing}
                            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', background: 'rgba(201,168,76,0.12)', border: '1px solid rgba(201,168,76,0.35)', borderRadius: 'var(--radius)', color: 'var(--hp-gold)', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600, fontFamily: 'var(--font-body)' }}
                          >
                            <MapPin size={13} /> {finishGpsCapturing ? 'Getting…' : 'Use my location'}
                          </button>
                        </div>
                      </div>
                      <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 5 }}>Latitude</label>
                          <input className="form-input" type="text" inputMode="decimal" value={finishZoneLat} onChange={e => setFinishZoneLat(e.target.value)} style={{ fontFamily: 'var(--font-mono)', fontSize: '0.85rem' }} />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 5 }}>Longitude</label>
                          <input className="form-input" type="text" inputMode="decimal" value={finishZoneLng} onChange={e => setFinishZoneLng(e.target.value)} style={{ fontFamily: 'var(--font-mono)', fontSize: '0.85rem' }} />
                        </div>
                      </div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', marginTop: 8 }}>Default covers postcodes 2000, 2007, 2010, 2011, 2037, 2038, 2042, 2050.</div>
                    </div>

                    {/* Zone radius */}
                    <div className="form-group" style={{ maxWidth: '260px' }}>
                      <label className="form-label">Finish Zone Radius (metres)</label>
                      <input className="form-input" type="number" min="500" step="100" value={finishZoneRadiusM} onChange={e => setFinishZoneRadiusM(e.target.value)} />
                      <div style={{ marginTop: 6, fontSize: '0.75rem', color: 'var(--hp-dim)' }}>Default 4000m covers all delivery postcodes.</div>
                    </div>
                  </div>
                </div>

                {/* Radius input */}
                <div className="form-group" style={{ maxWidth: '260px', marginBottom: '24px', opacity: geoEnabled ? 1 : 0.4 }}>
                  <label className="form-label">Start Zone Distance (metres)</label>
                  <input
                    className="form-input"
                    type="number"
                    min="1"
                    step="1"
                    value={geoRadiusM}
                    disabled={!geoEnabled}
                    onChange={e => setGeoRadiusM(e.target.value)}
                  />
                  <div style={{ marginTop: '6px', fontSize: '0.75rem', color: 'var(--hp-dim)' }}>
                    Default is 150 m. Only applies when GPS geofence is enabled.
                  </div>
                </div>

                {/* Save result */}
                {geoSaveResult && (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    padding: '12px 16px', marginBottom: '16px',
                    borderRadius: 'var(--radius)',
                    background: geoSaveResult.ok ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
                    border: `1px solid ${geoSaveResult.ok ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}`,
                    color: geoSaveResult.ok ? 'var(--hp-green)' : 'var(--hp-red)',
                    fontSize: '0.875rem',
                  }}>
                    {geoSaveResult.ok ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
                    {geoSaveResult.msg}
                  </div>
                )}

                <button
                  className="btn btn-primary"
                  onClick={handleSaveGeofence}
                  disabled={geoSaving}
                >
                  {geoSaving
                    ? <><Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> Saving…</>
                    : <><MapPin size={14} /> Save Geofence Settings</>}
                </button>
              </>
            )}

            {activeTab === 'qroverride' && (
              <QROverridePanel personnel={personnel} />
            )}

            {activeTab === 'team-leaders' && (
              <TeamLeaderAccessPanel addNotification={addNotification} currentUser={currentUser} />
            )}

            {activeTab === 'notifications' && (
              <MessagesAlertsPanel addNotification={addNotification} />
            )}

            {activeTab === 'security' && (
              <div style={{ textAlign: 'center', padding: '40px', color: 'var(--hp-dim)' }}>
                <Settings size={40} style={{ marginBottom: '12px', opacity: 0.3 }} />
                <p>Security settings — coming in next release</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
